import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminImageUpload from '@/components/feature/AdminImageUpload';

export interface ConsultingServiceItem {
  icon: string;
  title: string;
  color: string;
  image: string;
  desc: string;
  benefits: string[];
  forWho: string;
  buttonText?: string;
  buttonLink?: string;
}

export interface ConsultingServicesData {
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  subtitle: string;
  items: ConsultingServiceItem[];
}

interface ServiceBlockProps {
  service: ConsultingServiceItem;
  index: number;
  consultingUrl: string;
  isEditing?: boolean;
  onChange?: (updated: ConsultingServiceItem) => void;
  onDelete?: () => void;
}

const ServiceBlock: React.FC<ServiceBlockProps> = ({
  service,
  index,
  consultingUrl,
  isEditing = false,
  onChange,
  onDelete,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  const handleBenefitChange = (bIdx: number, val: string) => {
    if (!onChange) return;
    const updated = service.benefits.map((b, i) => (i === bIdx ? val : b));
    onChange({ ...service, benefits: updated });
  };

  const handleAddBenefit = () => {
    if (!onChange) return;
    onChange({ ...service, benefits: [...service.benefits, 'Nuevo beneficio clave'] });
  };

  const handleRemoveBenefit = (bIdx: number) => {
    if (!onChange) return;
    onChange({ ...service, benefits: service.benefits.filter((_, i) => i !== bIdx) });
  };

  const buttonText = service.buttonText || 'Reservar una Consulta';
  const effectiveUrl = service.buttonLink || consultingUrl;

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-center transition-all duration-700 relative group ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {/* Delete button in edit mode */}
      {isEditing && onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute -top-3 right-0 z-20 px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1 shadow-lg cursor-pointer"
        >
          <i className="ri-delete-bin-line" />
          Eliminar Servicio
        </button>
      )}

      {/* Image */}
      <div className="w-full lg:w-[45%] flex-shrink-0">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-60 md:h-72 object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030006]/70 via-transparent to-transparent" />
          <div
            className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ background: `${service.color}25`, border: `1px solid ${service.color}40`, color: service.color }}
          >
            <i className={`${service.icon} text-sm`} />
            {service.title}
          </div>
        </div>

        {isEditing && (
          <div className="mt-3">
            <AdminImageUpload
              label="Imagen de Servicio"
              value={service.image}
              onChange={(imgUrl) => onChange && onChange({ ...service, image: imgUrl })}
              folder="consulting"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 w-full">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
          style={{ background: `${service.color}15`, border: `1px solid ${service.color}30` }}
        >
          <i className={`${service.icon} text-xl`} style={{ color: service.color }} />
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Título del Servicio</label>
                <input
                  type="text"
                  value={service.title}
                  onChange={(e) => onChange && onChange({ ...service, title: e.target.value })}
                  className="w-full bg-black/40 border border-purple-500/40 rounded-lg px-3 py-2 text-white font-bold text-lg focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Color HEX (#8c52ff, etc.)</label>
                <input
                  type="text"
                  value={service.color}
                  onChange={(e) => onChange && onChange({ ...service, color: e.target.value })}
                  className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Descripción</label>
              <textarea
                value={service.desc}
                onChange={(e) => onChange && onChange({ ...service, desc: e.target.value })}
                rows={3}
                className="w-full bg-black/40 border border-purple-500/30 rounded-lg p-2.5 text-white/80 text-sm focus:outline-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] text-white/50 uppercase tracking-wider">Beneficios / Entregables</label>
                <button
                  type="button"
                  onClick={handleAddBenefit}
                  className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 cursor-pointer font-medium"
                >
                  <i className="ri-add-line" /> Añadir Beneficio
                </button>
              </div>
              <div className="space-y-2">
                {service.benefits.map((b, bIdx) => (
                  <div key={bIdx} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={b}
                      onChange={(e) => handleBenefitChange(bIdx, e.target.value)}
                      className="flex-1 bg-black/40 border border-white/15 rounded px-2.5 py-1 text-white/80 text-xs focus:outline-none focus:border-purple-500"
                      placeholder={`Beneficio ${bIdx + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveBenefit(bIdx)}
                      className="p-1 text-red-400 hover:text-red-300 rounded cursor-pointer"
                      title="Eliminar beneficio"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider block mb-1">Para quién es</label>
              <input
                type="text"
                value={service.forWho}
                onChange={(e) => onChange && onChange({ ...service, forWho: e.target.value })}
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-white/70 text-xs focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div>
                <label className="text-[11px] text-purple-400 uppercase tracking-wider block mb-1">Texto del Botón</label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => onChange && onChange({ ...service, buttonText: e.target.value })}
                  className="w-full bg-black/40 border border-purple-500/40 rounded px-2.5 py-1.5 text-white text-xs focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[11px] text-cyan-400 uppercase tracking-wider block mb-1">Enlace del Botón</label>
                <input
                  type="text"
                  value={effectiveUrl}
                  onChange={(e) => onChange && onChange({ ...service, buttonLink: e.target.value })}
                  className="w-full bg-black/40 border border-cyan-500/40 rounded px-2.5 py-1.5 text-white text-xs focus:outline-none"
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-xl md:text-2xl font-black text-white mb-3">{service.title}</h2>
            <p className="text-white/55 text-sm leading-relaxed mb-5">{service.desc}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
              {service.benefits.map((b, bIdx) => (
                <div key={bIdx} className="flex items-start gap-2">
                  <div
                    className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                    style={{ background: `${service.color}20` }}
                  >
                    <i className="ri-check-line text-[10px]" style={{ color: service.color }} />
                  </div>
                  <span className="text-white/55 text-xs leading-relaxed">{b}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/3 border border-white/8 rounded-xl p-3.5 mb-5">
              <span className="text-white/40 text-xs uppercase tracking-wider font-bold">Para quién es: </span>
              <span className="text-white/55 text-xs leading-relaxed">{service.forWho}</span>
            </div>

            {effectiveUrl.startsWith('http') ? (
              <a
                href={effectiveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-sm"
                style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}
              >
                <i className="ri-calendar-line" />
                {buttonText}
              </a>
            ) : (
              <Link
                to={effectiveUrl}
                className="whitespace-nowrap inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-sm"
                style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}
              >
                <i className="ri-calendar-line" />
                {buttonText}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface ConsultingServicesProps {
  data: ConsultingServicesData;
  consultingUrl: string;
  isEditing?: boolean;
  onChange?: (data: ConsultingServicesData) => void;
}

const ConsultingServices: React.FC<ConsultingServicesProps> = ({
  data,
  consultingUrl,
  isEditing = false,
  onChange,
}) => {
  const handleServiceChange = (index: number, updatedItem: ConsultingServiceItem) => {
    if (!onChange) return;
    const updatedList = data.items.map((item, i) => (i === index ? updatedItem : item));
    onChange({ ...data, items: updatedList });
  };

  const handleAddService = () => {
    if (!onChange) return;
    const newService: ConsultingServiceItem = {
      icon: 'ri-building-line',
      title: 'Nuevo Servicio de Consultoría',
      color: '#8c52ff',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=700&h=450&fit=crop',
      desc: 'Asesoría especializada para la constitución, estructuración y expansión comercial.',
      benefits: ['Guía paso a paso', 'Estructura personalizada', 'Documentación legal inicial'],
      forWho: 'Emprendedores listos para estructurar su negocio formalmente.',
      buttonText: 'Reservar una Consulta',
      buttonLink: consultingUrl,
    };
    onChange({ ...data, items: [...data.items, newService] });
  };

  const handleDeleteService = (index: number) => {
    if (!onChange) return;
    onChange({ ...data, items: data.items.filter((_, i) => i !== index) });
  };

  return (
    <section id="services" className="relative py-24 bg-[#030006] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse,rgba(140,82,255,0.06)_0%,transparent_70%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-[#8c52ff]/10 border border-[#8c52ff]/30 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-sparkling-line text-[#8c52ff] text-xs" />
            {isEditing ? (
              <input
                type="text"
                value={data.badge}
                onChange={(e) => onChange && onChange({ ...data, badge: e.target.value })}
                className="bg-black/40 border border-purple-500/40 rounded px-2 text-[#8c52ff] text-xs font-bold uppercase tracking-widest focus:outline-none"
              />
            ) : (
              <span className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest">{data.badge}</span>
            )}
          </div>

          {isEditing ? (
            <div className="max-w-xl mx-auto space-y-3 mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={data.titlePrefix}
                  onChange={(e) => onChange && onChange({ ...data, titlePrefix: e.target.value })}
                  className="flex-1 bg-black/40 border border-white/20 rounded px-3 py-2 text-white font-black text-2xl text-right focus:outline-none"
                />
                <input
                  type="text"
                  value={data.titleHighlight}
                  onChange={(e) => onChange && onChange({ ...data, titleHighlight: e.target.value })}
                  className="flex-1 bg-black/40 border border-purple-500/40 rounded px-3 py-2 text-purple-400 font-black text-2xl text-left focus:outline-none"
                />
              </div>
              <textarea
                value={data.subtitle}
                onChange={(e) => onChange && onChange({ ...data, subtitle: e.target.value })}
                rows={2}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-1.5 text-white/60 text-sm text-center focus:outline-none"
              />
            </div>
          ) : (
            <>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                {data.titlePrefix}{' '}
                <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">
                  {data.titleHighlight}
                </span>
              </h2>
              <p className="text-white/50 text-base max-w-xl mx-auto">
                {data.subtitle}
              </p>
            </>
          )}

          {/* Add Service button in edit mode */}
          {isEditing && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleAddService}
                className="px-5 py-2.5 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] hover:opacity-90 text-white rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg shadow-purple-500/30 cursor-pointer"
              >
                <i className="ri-add-circle-line text-lg" />
                Añadir Nuevo Servicio de Consultoría
              </button>
            </div>
          )}
        </div>

        {/* Services List */}
        <div className="space-y-20">
          {data.items.map((service, i) => (
            <ServiceBlock
              key={i}
              service={service}
              index={i}
              consultingUrl={consultingUrl}
              isEditing={isEditing}
              onChange={(updated) => handleServiceChange(i, updated)}
              onDelete={() => handleDeleteService(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ConsultingServices;
